import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import ContactButtons from "@/components/ContactButtons";

export default function CallbackForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Thank you — our concierge will call you shortly.");
      setForm({ name: "", phone: "", email: "", message: "", company: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-[#1a1a1d] border-white/15 text-white focus-visible:ring-[#D4AF37] rounded-sm h-12";

  return (
    <section id="callback" className="max-w-7xl mx-auto px-6 py-28" data-testid="callback-section">
      <div className="grid grid-cols-1 gap-0 rounded-sm overflow-hidden border border-white/10">
        <div className="bg-black/50 backdrop-blur-xl p-10 sm:p-14">
          <p className="text-xs tracking-label uppercase gold-text mb-4">Not Ready to Book?</p>
          <h2 className="font-heading text-4xl font-light tracking-tight leading-tight">
            Request a callback
          </h2>
          <p className="text-zinc-400 font-light mt-4 leading-relaxed">
            Leave your details and our concierge will reach out to craft the perfect journey for you.
          </p>

          {done ? (
            <div className="mt-10 bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-6 text-emerald-300" data-testid="callback-success">
              <p className="font-light">Request received. We'll be in touch very soon.</p>
              <button onClick={() => setDone(false)} data-testid="callback-another" className="mt-3 text-sm text-emerald-400 hover:underline">Send another request</button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs mb-1.5 block">Name</Label>
                  <Input data-testid="callback-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs mb-1.5 block">Phone</Label>
                  <Input data-testid="callback-phone" className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1 555 000 0000" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">Email (optional)</Label>
                <Input data-testid="callback-email" type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">How can we help?</Label>
                <Textarea data-testid="callback-message" rows={3} className="bg-[#1a1a1d] border-white/15 text-white focus-visible:ring-[#D4AF37] rounded-sm" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Occasion, date…" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="callback-submit-btn"
                className="flex items-center justify-center gap-2 w-fit bg-[#D4AF37] text-black hover:bg-[#E0BC4A] disabled:opacity-50 transition-all duration-300 rounded-sm px-[3px] py-[1px] font-medium tracking-wide"
              >
                {loading ? "Sending…" : <>Request Callback <Send size={16} /></>}
              </button>
              <Link
                to="/policies"
                data-testid="callback-view-policies-btn"
                className="flex items-center justify-center gap-2 w-fit bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-[3px] py-[1px] font-medium tracking-wide"
              >
                View Our Policies
              </Link>
              <ContactButtons className="pt-2" />
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
